<form [formGroup]="form" (submit)="submitForm()">
  <mat-card class="p-4 bg-grey-button form-income-verification">
    <h2>Income Verification</h2>

    <p class="form-income-verification__subtitle">
      Please provide information about your household and financial situation.
    </p>

    <section class="form-income-verification__section">
      <h3>Section 1 - Household Members</h3>

      <p>List all people who live in your household.</p>

      <button
        id="addHouseholdMemberButton"
        data-pw="add_household_member_button"
        data-qa="add_household_member_button"
        [attr.aria-label]="'Add Household Member'"
        mat-stroked-button 
        color="primary"
        type="button"
        class="mb-3"
        (click)="addHouseholdMember()"
        [disabled]="isReadOnly">
        + Add Household Member
      </button>

      <div formArrayName="householdMembers">
        @for (member of householdMembers.controls; track member; let i = $index) {
          <div [formGroupName]="i" class="form-income-verification__member-card">
            <div class="edit-field">
              <label [for]="'householdMemberFullNameInput_' + i" [attr.aria-label]="'Full Name'">
                Full Name
              </label>

              <input
                [id]="'householdMemberFullNameInput_' + i"
                [attr.data-pw]="'household_member_full_name_input_' + i"
                [attr.data-qa]="'household_member_full_name_input_' + i"
                [attr.aria-label]="'Full Name'"
                matInput
                formControlName="fullName" />
            </div>

            <div class="edit-field">
              <label [for]="'householdMemberAgeInput_' + i" [attr.aria-label]="'Age'">
                Age
              </label>

              <input
                [id]="'householdMemberAgeInput_' + i"
                [attr.data-pw]="'household_member_age_input_' + i"
                [attr.data-qa]="'household_member_age_input_' + i"
                [attr.aria-label]="'Age'"
                matInput
                type="number"
                formControlName="age" />
            </div>

            <div class="edit-field">
              <label
                [for]="'householdMemberRelationshipInput_' + i"
                [attr.aria-label]="'Relationship to Student'">
                Relationship to Student
              </label>

              <input
                [id]="'householdMemberRelationshipInput_' + i"
                [attr.data-pw]="'household_member_relationship_input_' + i"
                [attr.data-qa]="'household_member_relationship_input_' + i"
                [attr.aria-label]="'Relationship to Student'"
                matInput
                formControlName="relationshipToStudent" />
            </div>

            <div class="form-income-verification__radio-question">
              <label
                [attr.for]="'householdMemberHadIncomeRadioGroup_' + i"
                [attr.aria-label]="'Did this person have income in ' + taxYear + '?'">
                Did this person have income in {{ taxYear }}?
              </label>

              <mat-radio-group
                [id]="'householdMemberHadIncomeRadioGroup_' + i"
                [attr.data-pw]="'household_member_had_income_radio_group_' + i"
                [attr.data-qa]="'household_member_had_income_radio_group_' + i"
                [attr.aria-label]="'Did this person have income in ' + taxYear + '?'"
                formControlName="hadIncome">
                <mat-radio-button
                  [id]="'householdMemberHadIncomeYes_' + i"
                  [attr.data-pw]="'household_member_had_income_yes_' + i"
                  [attr.data-qa]="'household_member_had_income_yes_' + i"
                  [attr.aria-label]="'Yes'"
                  [value]="true">
                  Yes
                </mat-radio-button>

                <mat-radio-button
                  [id]="'householdMemberHadIncomeNo_' + i"
                  [attr.data-pw]="'household_member_had_income_no_' + i"
                  [attr.data-qa]="'household_member_had_income_no_' + i"
                  [attr.aria-label]="'No'"
                  [value]="false">
                  No
                </mat-radio-button>
              </mat-radio-group>
            </div>

            <div class="form-income-verification__radio-question">
              <label
                [attr.for]="'householdMemberFiledTaxesRadioGroup_' + i"
                [attr.aria-label]="'Did this person file a ' + taxYear + ' Federal tax return?'">
                Did this person file a {{ taxYear }} Federal tax return?
              </label>

              <mat-radio-group
                [id]="'householdMemberFiledTaxesRadioGroup_' + i"
                [attr.data-pw]="'household_member_filed_taxes_radio_group_' + i"
                [attr.data-qa]="'household_member_filed_taxes_radio_group_' + i"
                [attr.aria-label]="'Did this person file a ' + taxYear + ' Federal tax return?'"
                formControlName="filedFederalTaxReturn">
                <mat-radio-button
                  [id]="'householdMemberFiledTaxesYes_' + i"
                  [attr.data-pw]="'household_member_filed_taxes_yes_' + i"
                  [attr.data-qa]="'household_member_filed_taxes_yes_' + i"
                  [attr.aria-label]="'Yes'"
                  [value]="true">
                  Yes
                </mat-radio-button>

                <mat-radio-button
                  [id]="'householdMemberFiledTaxesNo_' + i"
                  [attr.data-pw]="'household_member_filed_taxes_no_' + i"
                  [attr.data-qa]="'household_member_filed_taxes_no_' + i"
                  [attr.aria-label]="'No'"
                  [value]="false">
                  No
                </mat-radio-button>
              </mat-radio-group>
            </div>

            <button
              [id]="'removeHouseholdMemberButton_' + i"
              [attr.data-pw]="'remove_household_member_button_' + i"
              [attr.data-qa]="'remove_household_member_button_' + i"
              [attr.aria-label]="'Remove Household Member'"
              mat-button
              color="warn"
              type="button"
              (click)="removeHouseholdMember(i)"
              [disabled]="isReadOnly">
              Remove
            </button>
          </div>
        }
      </div>

      @if (householdMembers.length > 0) {
        <table class="form-income-verification__summary">
          <thead>
            <tr>
              <th>Name</th>
              <th>Age</th>
              <th>Relationship</th>
              <th>Has Income</th>
              <th>Filed Taxes</th>
            </tr>
          </thead>

          <tbody>
            @for (member of householdMembers.getRawValue(); track member.fullName; let i = $index) {
              <tr>
                <td
                  [id]="'summaryMemberName_' + i"
                  [attr.data-pw]="'summary_member_name_' + i"
                  [attr.data-qa]="'summary_member_name_' + i"
                  [attr.aria-label]="member.fullName">
                  {{ member.fullName }}
                </td>

                <td
                  [id]="'summaryMemberAge_' + i"
                  [attr.data-pw]="'summary_member_age_' + i"
                  [attr.data-qa]="'summary_member_age_' + i"
                  [attr.aria-label]="member.age">
                  {{ member.age }}
                </td>

                <td
                  [id]="'summaryMemberRelationship_' + i"
                  [attr.data-pw]="'summary_member_relationship_' + i"
                  [attr.data-qa]="'summary_member_relationship_' + i"
                  [attr.aria-label]="member.relationshipToStudent">
                  {{ member.relationshipToStudent }}
                </td>

                <td
                  [id]="'summaryMemberHasIncome_' + i"
                  [attr.data-pw]="'summary_member_has_income_' + i"
                  [attr.data-qa]="'summary_member_has_income_' + i"
                  [attr.aria-label]="member.hadIncome ? 'Yes' : 'No'">
                  {{ member.hadIncome ? 'Yes' : 'No' }}
                </td>

                <td
                  [id]="'summaryMemberFiledTaxes_' + i"
                  [attr.data-pw]="'summary_member_filed_taxes_' + i"
                  [attr.data-qa]="'summary_member_filed_taxes_' + i"
                  [attr.aria-label]="member.filedFederalTaxReturn ? 'Yes' : 'No'">
                  {{ member.filedFederalTaxReturn ? 'Yes' : 'No' }}
                </td>
              </tr>
            }
          </tbody>
        </table>
      }
    </section>

    <section class="form-income-verification__section">
      <h3>Section 2 - Financial Information</h3>

      <p>Answer the following questions about your household in {{ taxYear }}.</p>

      @for (question of financialQuestions; track question.key) {
        <div class="form-income-verification__question">
          <label
            [attr.for]="question.key + 'RadioGroup'"
            [attr.aria-label]="question.label">
            {{ question.label }}
          </label>

          <mat-radio-group
            [id]="question.key + 'RadioGroup'"
            [attr.data-pw]="question.key + '_radio_group'"
            [attr.data-qa]="question.key + '_radio_group'"
            [attr.aria-label]="question.label"
            [formControlName]="question.key">
            <mat-radio-button
              [id]="question.key + 'Yes'"
              [attr.data-pw]="question.key + '_yes'"
              [attr.data-qa]="question.key + '_yes'"
              [attr.aria-label]="'Yes'"
              [value]="true">
              Yes
            </mat-radio-button>

            <mat-radio-button
              [id]="question.key + 'No'"
              [attr.data-pw]="question.key + '_no'"
              [attr.data-qa]="question.key + '_no'"
              [attr.aria-label]="'No'"
              [value]="false">
              No
            </mat-radio-button>
          </mat-radio-group>
        </div>
      }

      <h4>Resources</h4>

      <div class="form-income-verification__resource-grid">
        @for (question of resourceQuestions; track question.key) {
          <div class="form-income-verification__question">
            <label
              [attr.for]="question.key + 'RadioGroup'"
              [attr.aria-label]="question.label">
              {{ question.label }}
            </label>

            <mat-radio-group
              [id]="question.key + 'RadioGroup'"
              [attr.data-pw]="question.key + '_radio_group'"
              [attr.data-qa]="question.key + '_radio_group'"
              [attr.aria-label]="question.label"
              [formControlName]="question.key">
              <mat-radio-button
                [id]="question.key + 'Yes'"
                [attr.data-pw]="question.key + '_yes'"
                [attr.data-qa]="question.key + '_yes'"
                [attr.aria-label]="'Yes'"
                [value]="true">
                Yes
              </mat-radio-button>

              <mat-radio-button
                [id]="question.key + 'No'"
                [attr.data-pw]="question.key + '_no'"
                [attr.data-qa]="question.key + '_no'"
                [attr.aria-label]="'No'"
                [value]="false">
                No
              </mat-radio-button>
            </mat-radio-group>
          </div>
        }
      </div>
    </section>

    <section class="form-income-verification__section">
      <h3>Section 3 - Certification</h3>

      <mat-checkbox
        id="certifiedCheckbox"
        data-pw="certified_checkbox"
        data-qa="certified_checkbox"
        [attr.aria-label]="'I certify that all of the information provided is complete and accurate.'"
        formControlName="certified">
        I certify that all of the information provided is complete and accurate.
      </mat-checkbox>
    </section>

    @if (!form.disabled) {
      <div class="button-row">
        <button
          id="cancelButton"
          data-pw="cancel_button"
          data-qa="cancel_button"
          [attr.aria-label]="'Cancel'"
          mat-stroked-button
          type="button"
          (click)="onBack()">
          Cancel
        </button>

        <button
          id="submitButton"
          data-pw="submit_button"
          data-qa="submit_button"
          [attr.aria-label]="'Submit'"
          mat-flat-button
          color="primary"
          type="submit"
          [disabled]="!isFormReady()">
          Submit
        </button>
      </div>
    }
  </mat-card>
</form>